import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { fetchApi } from "@/lib/api";
import { MentorshipDetails } from "@/lib/api/mentorships.ts";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  RotateCw,
  Filter,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { NewQuotedMessage } from "./quoted-message";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { EveryMessage } from "@sendbird/uikit-react/types/types";
import { CustomizeAITraitsModal } from "./ai-traits/ai-traits-button";

interface AISuggestionFeedbackDataType {
  text: string;
  approach?: string;
  triggeringMessageId: string | null;
  suggestion_type: "default" | "specific" | "priority";
  quoted_message_text?: string;
}

interface AISuggestedRepliesProps {
  mentorship: MentorshipDetails;
  onUiSuggestionClick: (text: string, approach?: string) => void;
  quotedMessage?: EveryMessage | null;
  handleRemoveQuote?: () => void;
  onFeedbackSuggestionClick: (data: AISuggestionFeedbackDataType) => void;
  onFeedbackDiscardVisibleSuggestion: (
    data: AISuggestionFeedbackDataType,
  ) => void;
  onFeedbackRequestNewApproach: () => void;
  onCurrentSuggestionChange?: (suggestion: CurrentAISuggestion | null) => void;
}

interface SuggestedMessageDataDefault {
  type: "default";
  data: {
    statement: string;
    question: string;
    reasoning: string;
  };
}

interface SuggestedMessageDataSpecific {
  type: "specific";
  data: {
    draft: string;
    reasoning: string;
  };
}

interface SuggestedMessageDataPriority {
  type: "priority";
  data: {
    draft: string;
    reasoning: string;
    nextAction: string;
  };
}

type SuggestedMessage =
  | SuggestedMessageDataDefault
  | SuggestedMessageDataSpecific
  | SuggestedMessageDataPriority;

interface SuggestedMessageLayoutProps {
  reasoning?: string;
  isReasoningOpen: boolean;
  setIsReasoningOpen: (isOpen: boolean) => void;
  handleNewApproachClick: () => void;
  children: React.ReactNode;
  isPriority?: boolean;
  extraActions?: React.ReactNode;
}

function SuggestedMessageLayout({
  reasoning,
  isReasoningOpen,
  setIsReasoningOpen,
  handleNewApproachClick,
  children,
  isPriority,
  extraActions,
}: SuggestedMessageLayoutProps) {
  return (
    <div className="flex flex-col gap-4 p-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Suggested message</h2>
          {isPriority && <Badge variant="destructive">Priority</Badge>}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="secondary"
                  className="cursor-pointer bg-blue-100 text-blue-800 hover:bg-blue-200"
                >
                  Beta
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-64">
                  This feature is currently in beta, please carefully monitor
                  the responses before sending to your teen!
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleNewApproachClick}>
            <RotateCw className="mr-2 h-4 w-4 cursor-pointer" />
            New Approach
          </Button>
          {extraActions}
        </div>
      </div>

      {reasoning && (
        <div className="flex flex-col gap-1">
          <div
            className="flex cursor-pointer items-center gap-1 text-sm text-gray-600"
            onClick={() => setIsReasoningOpen(!isReasoningOpen)}
          >
            Reasoning
            {isReasoningOpen ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </div>
          {isReasoningOpen && (
            <p className="rounded-md border bg-gray-50 p-2 text-sm text-gray-700">
              {reasoning}
            </p>
          )}
        </div>
      )}
      {/* Suggestion cards will be rendered here */}
      {children}
    </div>
  );
}

export type CurrentAISuggestion =
  | {
      type: "default";
      statement: string;
      question: string;
      reasoning: string;
      suggestion_type: "default";
      quoted_message_text?: string;
      triggeringMessageId: string | null;
    }
  | {
      type: "specific" | "priority";
      draft: string;
      reasoning: string;
      suggestion_type: "specific" | "priority";
      quoted_message_text?: string;
      triggeringMessageId: string | null;
    };

export function AISuggestedReplies({
  mentorship,
  onUiSuggestionClick,
  quotedMessage,
  handleRemoveQuote,
  onFeedbackSuggestionClick,
  onFeedbackDiscardVisibleSuggestion,
  onFeedbackRequestNewApproach,
  onCurrentSuggestionChange,
}: AISuggestedRepliesProps) {
  const [suggestedMessage, setSuggestedMessage] =
    useState<SuggestedMessage | null>(null);
  const [isReasoningOpen, setIsReasoningOpen] = useState(false);
  const [showNewApproachSelection, setShowNewApproachSelection] =
    useState(false);
  const [currentMessageStyle, setCurrentMessageStyle] = useState<
    string | undefined
  >(undefined);
  const [promptingMessageId, setPromptingMessageId] = useState<string | null>(
    null,
  );
  const [tone] = useState<"default" | "gpt">(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("ai-suggested-replies-tone");
      if (stored === "gpt" || stored === "default") return stored;
    }
    return "default";
  });
  const [isTonePopoverOpen, setIsTonePopoverOpen] = useState(false);
  const channelStore = useChannelContext();
  const channel = channelStore?.currentGroupChannel;

  const lastMessage = channel?.lastMessage as EveryMessage;

  const isLastMessageFromMentee =
    lastMessage?.sender?.userId === mentorship?.mentee?.id;

  const [isFetchingSuggestedReplies, setIsFetchingSuggestedReplies] =
    useState(false);

  const fetchSuggestedReplies = useCallback(
    async (
      forceFetch = false,
      newSuggestionStyle?: string,
      overrideTone?: "default" | "gpt",
    ) => {
      setIsFetchingSuggestedReplies(true);
      if (forceFetch || newSuggestionStyle) {
        setSuggestedMessage(null);
      }

      const messageIdForThisFetch =
        channel?.lastMessage?.messageId || quotedMessage?.messageId || null;

      try {
        const params: Record<string, unknown> = {
          force: forceFetch,
          suggestionStyle: newSuggestionStyle,
          selectedMessageText: quotedMessage?.message || undefined,
        };
        const selectedTone = overrideTone ?? tone;
        if (selectedTone === "gpt") {
          params.tone = "basic";
        }
        const { data } = await fetchApi<SuggestedMessage>({
          url: `v2/messages/${mentorship?.id}/suggested-replies`,
          method: "GET",
          params,
        });

        setIsFetchingSuggestedReplies(false);
        setSuggestedMessage(data);
        setPromptingMessageId(messageIdForThisFetch);

        if (newSuggestionStyle) {
          setShowNewApproachSelection(false);
        }
      } catch (error) {
        setIsFetchingSuggestedReplies(false);
      }
    },
    [
      mentorship?.id,
      quotedMessage?.message,
      channel?.lastMessage?.messageId,
      tone,
    ],
  );

  const handleGlobalNewApproachClick = () => {
    // If a suggestion is currently displayed, track as skipped before resetting
    if (suggestedMessage && suggestedMessage.data) {
      // Build the feedback data for the current suggestion
      let feedbackData: AISuggestionFeedbackDataType | null = null;
      const currentSuggestionType = suggestedMessage.type;
      const quotedMessageText = quotedMessage?.message || undefined;
      const triggeringMessageId = promptingMessageId;
      if (
        suggestedMessage.type === "default" &&
        "question" in suggestedMessage.data
      ) {
        feedbackData = {
          text: suggestedMessage.data.statement,
          approach: "statement",
          triggeringMessageId,
          suggestion_type: currentSuggestionType,
          quoted_message_text: quotedMessageText,
        };
      } else if (
        (suggestedMessage.type === "specific" ||
          suggestedMessage.type === "priority") &&
        "draft" in suggestedMessage.data
      ) {
        feedbackData = {
          text: suggestedMessage.data.draft,
          approach: currentMessageStyle || suggestedMessage.type,
          triggeringMessageId,
          suggestion_type: currentSuggestionType,
          quoted_message_text: quotedMessageText,
        };
      }
      if (feedbackData) {
        onFeedbackDiscardVisibleSuggestion(feedbackData);
      }
    }
    onFeedbackRequestNewApproach();
    setSuggestedMessage(null);
    setCurrentMessageStyle(undefined);
    setShowNewApproachSelection(true);
  };

  useEffect(() => {
    if (
      isLastMessageFromMentee &&
      !isFetchingSuggestedReplies &&
      !suggestedMessage &&
      !showNewApproachSelection
    ) {
      fetchSuggestedReplies();
    }
  }, [
    isLastMessageFromMentee,
    fetchSuggestedReplies,
    isFetchingSuggestedReplies,
    suggestedMessage,
    showNewApproachSelection,
  ]);

  useEffect(() => {
    if (
      isLastMessageFromMentee &&
      !isFetchingSuggestedReplies &&
      !showNewApproachSelection &&
      suggestedMessage
    ) {
      fetchSuggestedReplies();
    }
    // Refetch when quotedMessage changes, but only if we already have a suggestion
  }, [quotedMessage?.message]);

  useEffect(() => {
    if (!suggestedMessage || !suggestedMessage.data) {
      onCurrentSuggestionChange?.(null);
      return;
    }
    if (
      suggestedMessage.type === "default" &&
      "question" in suggestedMessage.data
    ) {
      onCurrentSuggestionChange?.({
        type: "default",
        statement: suggestedMessage.data.statement,
        question: suggestedMessage.data.question,
        reasoning: suggestedMessage.data.reasoning,
        suggestion_type: "default",
        quoted_message_text: quotedMessage?.message || undefined,
        triggeringMessageId: promptingMessageId,
      });
    } else if (
      (suggestedMessage.type === "specific" ||
        suggestedMessage.type === "priority") &&
      "draft" in suggestedMessage.data
    ) {
      onCurrentSuggestionChange?.({
        type: suggestedMessage.type,
        draft: suggestedMessage.data.draft,
        reasoning: suggestedMessage.data.reasoning,
        suggestion_type: suggestedMessage.type,
        quoted_message_text: quotedMessage?.message || undefined,
        triggeringMessageId: promptingMessageId,
      });
    } else {
      onCurrentSuggestionChange?.(null);
    }
  }, [suggestedMessage, quotedMessage, onCurrentSuggestionChange]);

  if (isFetchingSuggestedReplies) {
    return (
      <>
        {quotedMessage && (
          <NewQuotedMessage
            message={quotedMessage}
            handleRemoveQuote={handleRemoveQuote}
          />
        )}
        <div className="flex flex-col items-center justify-center gap-2 p-4 text-sm text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Crafting reply...</span>
        </div>
      </>
    );
  }

  if (showNewApproachSelection) {
    return (
      <>
        {quotedMessage && (
          <NewQuotedMessage
            message={quotedMessage}
            handleRemoveQuote={handleRemoveQuote}
          />
        )}
        <NewApproachUI
          onSuggestedReplyClick={(style) => {
            fetchSuggestedReplies(true, style);
            setCurrentMessageStyle(style);
          }}
        />
      </>
    );
  }

  if (!isLastMessageFromMentee && !suggestedMessage) {
    return (
      <>
        {quotedMessage && (
          <NewQuotedMessage
            message={quotedMessage}
            handleRemoveQuote={handleRemoveQuote}
          />
        )}
        <NewApproachUI
          onSuggestedReplyClick={(style) => {
            fetchSuggestedReplies(true, style);
            setCurrentMessageStyle(style);
          }}
        />
      </>
    );
  }

  if (!suggestedMessage?.data) {
    return null;
  }

  if (
    suggestedMessage.type === "default" &&
    "question" in suggestedMessage.data
  ) {
    const { statement, question, reasoning } = suggestedMessage.data;
    const currentSuggestionType = suggestedMessage.type;
    const quotedMessageText = quotedMessage?.message || undefined;

    return (
      <SuggestedMessageLayout
        reasoning={reasoning}
        isReasoningOpen={isReasoningOpen}
        setIsReasoningOpen={setIsReasoningOpen}
        handleNewApproachClick={handleGlobalNewApproachClick}
        extraActions={
          <Button
            variant="outline"
            onClick={() => {
              setIsTonePopoverOpen((v) => !v);
            }}
          >
            <Filter size={18} />
            Edit AI
          </Button>
        }
      >
        {quotedMessage && (
          <NewQuotedMessage
            message={quotedMessage}
            handleRemoveQuote={handleRemoveQuote}
          />
        )}
        <div className="flex flex-row gap-2">
          <div
            className="flex flex-1 cursor-pointer flex-col gap-2 rounded-md border bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
            onClick={() => {
              onUiSuggestionClick(statement, "statement");
              onFeedbackSuggestionClick({
                text: statement,
                approach: "statement",
                triggeringMessageId: promptingMessageId,
                suggestion_type: currentSuggestionType,
                quoted_message_text: quotedMessageText,
              });
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Statement</h3>
              <RotateCw
                size={16}
                className="cursor-pointer text-gray-400 hover:text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onFeedbackDiscardVisibleSuggestion({
                    text: statement,
                    approach: "statement",
                    triggeringMessageId: promptingMessageId,
                    suggestion_type: currentSuggestionType,
                    quoted_message_text: quotedMessageText,
                  });
                  fetchSuggestedReplies(true);
                }}
              />
            </div>
            <p className="text-sm text-gray-700">{statement}</p>
          </div>
          <div
            className="flex flex-1 cursor-pointer flex-col gap-2 rounded-md border bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
            onClick={() => {
              onUiSuggestionClick(question, "question");
              onFeedbackSuggestionClick({
                text: question,
                approach: "question",
                triggeringMessageId: promptingMessageId,
                suggestion_type: currentSuggestionType,
                quoted_message_text: quotedMessageText,
              });
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Question</h3>
              <RotateCw
                size={16}
                className="cursor-pointer text-gray-400 hover:text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onFeedbackDiscardVisibleSuggestion({
                    text: question,
                    approach: "question",
                    triggeringMessageId: promptingMessageId,
                    suggestion_type: currentSuggestionType,
                    quoted_message_text: quotedMessageText,
                  });
                  fetchSuggestedReplies(true);
                }}
              />
            </div>
            <p className="text-sm text-gray-700">{question}</p>
          </div>
        </div>
        <div className="flex flex-col gap-4 p-2">
          <CustomizeAITraitsModal
            mentorship={mentorship}
            open={isTonePopoverOpen}
            onClose={() => setIsTonePopoverOpen(false)}
          />
        </div>
      </SuggestedMessageLayout>
    );
  }

  if (
    (suggestedMessage.type === "specific" ||
      suggestedMessage.type === "priority") &&
    "draft" in suggestedMessage.data
  ) {
    const { draft, reasoning } = suggestedMessage.data;
    const approachForFeedback = currentMessageStyle || suggestedMessage.type;
    const currentSuggestionType = suggestedMessage.type;
    const quotedMessageText = quotedMessage?.message || undefined;

    return (
      <SuggestedMessageLayout
        reasoning={reasoning}
        isReasoningOpen={isReasoningOpen}
        setIsReasoningOpen={setIsReasoningOpen}
        handleNewApproachClick={handleGlobalNewApproachClick}
        isPriority={suggestedMessage.type === "priority"}
        extraActions={
          <Button
            variant="outline"
            onClick={() => {
              setIsTonePopoverOpen((v) => !v);
            }}
          >
            <Filter size={18} />
            Edit AI
          </Button>
        }
      >
        {quotedMessage && (
          <NewQuotedMessage
            message={quotedMessage}
            handleRemoveQuote={handleRemoveQuote}
          />
        )}
        <div
          className="flex flex-1 cursor-pointer flex-col gap-2 rounded-md border bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
          onClick={() => {
            onUiSuggestionClick(draft, approachForFeedback);
            onFeedbackSuggestionClick({
              text: draft,
              approach: approachForFeedback,
              triggeringMessageId: promptingMessageId,
              suggestion_type: currentSuggestionType,
              quoted_message_text: quotedMessageText,
            });
          }}
        >
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Suggested Reply</h3>
            <RotateCw
              size={16}
              className="cursor-pointer text-gray-400 hover:text-gray-600"
              onClick={(e) => {
                e.stopPropagation();
                onFeedbackDiscardVisibleSuggestion({
                  text: draft,
                  approach: approachForFeedback,
                  triggeringMessageId: promptingMessageId,
                  suggestion_type: currentSuggestionType,
                  quoted_message_text: quotedMessageText,
                });
                fetchSuggestedReplies(true, currentMessageStyle);
              }}
            />
          </div>
          <p className="text-sm text-gray-700">{draft}</p>
        </div>
        <div className="flex flex-col gap-4 p-2">
          <CustomizeAITraitsModal
            mentorship={mentorship}
            open={isTonePopoverOpen}
            onClose={() => setIsTonePopoverOpen(false)}
          />
        </div>
      </SuggestedMessageLayout>
    );
  }

  return null;
}

function SuggestionButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      className="cursor-pointer rounded-full border px-4 py-2 shadow-sm transition-colors hover:bg-gray-100"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function NewApproachUI({
  onSuggestedReplyClick,
}: {
  onSuggestedReplyClick: (text: string) => void;
}) {
  const handleApproachClick = (approach: string) => {
    onSuggestedReplyClick(approach);
  };

  return (
    <div className="flex flex-row flex-wrap items-center justify-center gap-4 p-4">
      <SuggestionButton onClick={() => handleApproachClick("Validate")}>
        Validate
      </SuggestionButton>
      <SuggestionButton
        onClick={() => handleApproachClick("Offer Advice / Encouragement")}
      >
        Offer Advice / Encouragement
      </SuggestionButton>
      <SuggestionButton
        onClick={() => handleApproachClick("Share Lived Experience")}
      >
        Share Lived Experience
      </SuggestionButton>
      <SuggestionButton onClick={() => handleApproachClick("Ask a Question")}>
        Ask a Question
      </SuggestionButton>
      <SuggestionButton
        onClick={() =>
          handleApproachClick("Lighten the Mood / Build Connection")
        }
      >
        Lighten the Mood / Build Connection
      </SuggestionButton>
    </div>
  );
}
